<template>
  <section class="i-hero">
    <div class="hero__inner hero-anim">
      <!-- Eyebrow -->
      <div class="hero__eyebrow">
        <span class="hero__eyebrow-dot">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </span>
        Rated 4.8/5 from 6,847 sessions
      </div>

      <!-- Headline -->
      <h1
        v-if="!isCoachingUpsellBPage"
        class="hero__title"
      >
        Let a recruiter turn your results into a
        <em>90-day action plan.</em>
      </h1>
      <h1
        v-if="isCoachingUpsellBPage"
        class="hero__title"
      >
        Sit down with a recruiter who will show you
        <em>exactly what to do next.</em>
      </h1>

      <!-- Sub-headline -->
      <div class="hero__sub">
        Book a {{ timing }}-minute 1:1 with a former recruiter who knows exactly how people get hired — and why they
        don't.
        <strong>Your situation. Your next move. A personalized step-by-step plan you leave with.</strong>
      </div>

      <!-- Coach card -->
      <div class="coach-card">
        <a
          class="coach-card__book"
          href="#book"
          @click.prevent="$emit('book')"
        >
          Book {{ props.coachName }} →
        </a>
        <div class="coach-card__top">
          <div class="coach-card__photo">
            <img
              ref="imgRef"
              :src="props.coachPicture"
              alt="Coach"
              @error="onImgError"
            />
            <span
              class="coach-card__placeholder"
              :style="{ display: placeholderVisible ? 'flex' : 'none' }"
              >M</span
            >
          </div>
          <div class="coach-card__info">
            <div class="coach-card__name">{{ props.coachName }}</div>
            <div class="coach-card__title">Former Head of Talent Acquisition</div>
          </div>
        </div>
        <div class="coach-card__logos">
          <img
            class="coach-card__logo"
            :src="logoZappos"
            alt="Zappos"
          />
          <img
            class="coach-card__logo"
            :src="logoHulu"
            alt="Hulu"
          />
          <img
            class="coach-card__logo"
            :src="logoAmazon"
            alt="Amazon"
          />
          <img
            class="coach-card__logo"
            :src="logoTacobell"
            alt="Taco Bell"
          />
        </div>
        <div class="coach-card__stats">
          <span class="coach-card__stat"><strong>15+</strong> years</span>
          <span class="coach-card__stat-divider"></span>
          <span class="coach-card__stat"
            ><span class="coach-card__star">★</span> <strong>4.8/5</strong> from 2k sessions</span
          >
          <span class="coach-card__stat-divider"></span>
          <span class="coach-card__stat coach-card__stat--accent"
            ><strong>40%</strong> avg. client salary increase</span
          >
        </div>
        <div
          v-if="!isCoachingUpsellBPage"
          class="coach-card__review"
        >
          "She told me exactly what to do next - <strong>I wish I had done this months earlier.</strong>" — Sam A.
        </div>
        <div
          v-if="isCoachingUpsellBPage"
          class="coach-card__review"
        >
          "<strong>I wish I had done this months earlier.</strong>" — Sam A.
        </div>
      </div>

      <!-- Post-card description -->
      <!-- <div class="hero__post-card">
        In {{ timing }} minutes, she'll <strong>walk you through your results</strong>,
        <strong>identify your strongest move</strong>, and
        <strong>build your step-by-step action plan with you</strong> — live on the call.
      </div> -->

      <!-- CTA block -->
      <div class="hero__cta-block">
        <a
          class="hero__cta-btn"
          href="#book"
          @click.prevent="$emit('book')"
        >
          Book your session&nbsp;&nbsp;<span class="hero__price-was">${{ highPricing }}</span
          >&nbsp;<span class="hero__price-now">${{ pricing }}</span>
        </a>
        <div class="hero__cta-right">
          <div class="hero__cta-savings">
            Save ${{ Math.round(parseInt(highPricing) - parseInt(pricing)) }} —
            {{ Math.round(((parseInt(highPricing) - parseInt(pricing)) * 100) / parseInt(highPricing)) }}% off today
            only
          </div>
          <div class="hero__cta-savings-detail">No upsell. No follow-up pitch. No subscription.</div>
        </div>
      </div>

      <!-- Median outcome -->
      <div class="hero__median-outcome">
        Median salary increase for clients who act on their session:
        <strong class="hero__highlight">$25,400</strong>. That's <strong class="hero__highlight">519x</strong> the cost
        of this conversation.
      </div>

      <!-- Rating -->
      <div class="hero__rating">
        <span class="hero__rating-star">★</span>
        <strong>4.8/5</strong> from 6,847 sessions
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { useRoute } from "vue-router"

import { GlobalVariables } from "@/helpers/globalVariables"

// ASSETS
import logoAmazon from "@/assets/logos/logo-amazon-black1.svg"
import logoHulu from "@/assets/logos/logo-hulu-black.svg"
import logoTacobell from "@/assets/logos/logo-tacobell-black.svg"
import logoZappos from "@/assets/logos/logo-zappos-black.svg"

defineEmits(["book"])

// PROPS
const props = defineProps({
  coachName: {
    type: String,
    default: null
  },
  coachPicture: {
    type: String,
    default: null
  },
  pricing: {
    type: String,
    default: "49"
  },
  timing: {
    type: String,
    default: "30"
  },
  highPricing: {
    type: String,
    default: "149.90"
  }
})

// ROUTES
const activeRoute = useRoute()

// REFS
const placeholderVisible = ref(false)

// COMPUTED
const isCoachingUpsellBPage = computed(
  () => activeRoute.name === GlobalVariables.urls.report.children.coachingUpsellB.routeName
)

// FUNCTIONS
const onImgError = () => {
  placeholderVisible.value = true
}
</script>

<style lang="scss" scoped>
.i-hero {
  background: var(--hero-bg);
  padding: 0 0 48px;
  border-top: none;
  border-bottom: 1px solid var(--hero-border);
  overflow: hidden;

  // override global section border-top
  &.i-hero {
    border-top: none;
  }

  .hero {
    &__inner {
      padding: 40px 20px 0;
      max-width: 760px;
      margin: 0 auto;

      @include respond-to($breakpoint-md) {
        padding: 56px 24px 0;
      }
    }

    &__eyebrow {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #10b9811a;
      border: 1px solid #10b98140;
      border-radius: 999px;
      padding: 8px 16px;
      font-size: 0.8rem;
      font-weight: 600;
      color: var(--green-dark);
      letter-spacing: 0.01em;
      margin-bottom: 28px;
    }

    &__eyebrow-dot {
      width: 20px;
      height: 20px;
      background: var(--green);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      svg {
        width: 11px;
        height: 11px;
        color: white;
      }
    }

    &__title {
      font-family: "DM Serif Display", serif;
      font-size: clamp(26px, 6.5vw, 50px);
      font-weight: 700;
      line-height: 1.2;
      color: var(--hero-text);
      letter-spacing: -0.01em;
      margin-bottom: 0;

      em {
        font-style: italic;
        font-weight: 700;
        color: var(--hero-accent);
        position: relative;
        display: inline;

        &::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: -2px;
          height: 6px;
          background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 8' preserveAspectRatio='none'%3E%3Cpath d='M0 5 Q10 1 20 5 Q30 9 40 5 Q50 1 60 5 Q70 9 80 5 Q90 1 100 5 Q110 9 120 5' fill='none' stroke='%235B4CFF' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")
            repeat-x;
          background-size: 60px 6px;
          opacity: 0.45;
        }
      }
    }

    &__sub {
      font-size: clamp(0.9rem, 2.5vw, 1.05rem);
      color: var(--hero-muted);
      line-height: 1.65;
      margin-top: 20px;
      max-width: 560px;

      strong {
        color: var(--hero-text);
        font-weight: 600;
      }
    }

    &__post-card {
      margin-top: 20px;
      font-size: 0.92rem;
      color: var(--text-body);
      line-height: 1.65;
      max-width: 560px;
    }

    &__cta-block {
      margin-top: 28px;
      display: flex;
      flex-flow: column wrap;
      align-items: stretch;
      gap: 14px;

      @include respond-to(500px) {
        flex-direction: unset;
        align-items: center;
        gap: 16px;
      }
    }

    &__cta-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 18px 30px;
      background: var(--grad-primary);
      color: white;
      font-family: "DM Sans", system-ui, sans-serif;
      font-size: 1rem;
      font-weight: 700;
      border: none;
      border-radius: var(--radius-sm);
      cursor: pointer;
      text-decoration: none;
      transition:
        transform 0.15s,
        box-shadow 0.15s;
      line-height: 1;
      box-shadow: 0 6px 28px #5b4cff4d;
      flex-shrink: 0;
      width: 100%;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 10px 36px #5b4cff66;
      }

      &:active {
        transform: translateY(0);
      }

      @include respond-to(500px) {
        width: unset;
      }
    }

    &__price-was {
      text-decoration: line-through;
      opacity: 0.45;
      font-weight: 400;
      font-size: 0.88rem;
    }

    &__price-now {
      background: #fff3;
      border: 1px solid #ffffff59;
      border-radius: 6px;
      padding: 3px 12px;
      font-size: 1.05rem;
      font-weight: 800;
    }

    &__cta-right {
      flex: 1;
      min-width: 160px;
    }

    &__cta-savings {
      display: inline-block;
      font-size: 0.82rem;
      font-weight: 700;
      color: var(--green-dark);
      background: var(--green-lt);
      border: 1px solid #10b98140;
      border-radius: 6px;
      padding: 5px 12px;
    }

    &__cta-savings-detail {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 0.8rem;
      color: var(--hero-muted);
      line-height: 1.4;
      margin-top: 8px;

      &::before {
        content: "✓";
        color: var(--green);
        font-weight: 700;
        font-size: 0.85rem;
      }
    }

    &__median-outcome {
      margin-top: 28px;
      background: #5b4cff0a;
      border-left: 3px solid var(--purple);
      border-radius: 0 8px 8px 0;
      padding: 14px 20px;
      font-size: 0.9rem;
      font-style: italic;
      color: var(--text-body);
      line-height: 1.6;
    }

    &__highlight {
      color: var(--purple);
      font-weight: 700;
      font-style: italic;
    }

    &__rating {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 16px;
      font-size: 0.82rem;
      color: var(--hero-muted);

      strong {
        color: var(--hero-text);
        font-weight: 700;
      }
    }

    &__rating-star {
      color: var(--star);
      font-size: 0.95rem;
    }
  }

  // ── Coach Card ───────────────────────────────────────
  .coach-card {
    margin-top: 28px;
    background: var(--white);
    border: 1px solid var(--hero-border);
    border-radius: 14px;
    padding: 18px 20px;
    position: relative;
    box-shadow: 0 2px 12px #5b4cff0f;

    @include respond-to($breakpoint-md) {
      padding: 20px 24px;
    }

    &__book {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: var(--purple);
      font-size: 0.78rem;
      font-weight: 700;
      text-decoration: underline;
      text-underline-offset: 3px;
      cursor: pointer;
      white-space: nowrap;
      position: absolute;
      top: 20px;
      right: 20px;
      background: none;
      border: none;
      padding: 0;
      transition: color 0.15s;

      &:hover {
        color: var(--purple-lt);
      }
    }

    &__top {
      display: flex;
      align-items: center;
      gap: 14px;
      padding-right: 80px;

      @include respond-to(500px) {
        padding-right: 100px;
      }

      @include respond-to($breakpoint-md) {
        padding-right: 0;
      }
    }

    &__photo {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: var(--grad-primary);
      border: 2px solid var(--purple);
      flex-shrink: 0;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__placeholder {
      font-size: 1.2rem;
      font-weight: 700;
      color: #ffffffd9;
      display: none;
      align-items: center;
      justify-content: center;
    }

    &__info {
      flex: 1;
      min-width: 0;
    }

    &__name {
      font-size: 1rem;
      font-weight: 700;
      color: var(--hero-text);
      margin-bottom: 2px;
    }

    &__title {
      font-size: 0.78rem;
      color: var(--hero-muted);
      line-height: 1.4;
    }

    &__logos {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      margin-top: 15px;
    }

    &__logo {
      display: inline-flex;
      align-items: center;
      height: 28px;

      @include respond-to(414px) {
        height: 35px;
      }

      @include respond-to(500px) {
        height: 40px;
      }
    }

    &__stats {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 12px;
      flex-wrap: wrap;
      padding-bottom: 10px;

      @include respond-to(500px) {
        gap: 12px;
        padding-bottom: 15px;
      }
    }

    &__stat {
      font-size: 0.78rem;
      color: var(--hero-muted);

      strong {
        color: var(--hero-text);
        font-weight: 700;
      }

      &--accent strong {
        color: var(--green-dark);
      }
    }

    &__stat-divider {
      width: 1px;
      height: 12px;
      background: var(--hero-border);
      flex-shrink: 0;
      display: none;

      @include respond-to(500px) {
        display: unset;
      }
    }

    &__review {
      font-size: 0.85rem;
      color: var(--hero-muted);
      line-height: 1.4;
      border-top: 1px solid var(--border);
      font-style: italic;
      padding-top: 10px;

      @include respond-to(500px) {
        padding-top: 15px;
      }
    }

    &__star {
      color: var(--star);
    }
  }
}
</style>
